import type { FormChatState } from "../repositories/formChat.repository";
import {
  buildCreateEventPayload,
  type BuildCreateEventPayloadInput,
} from "./buildCreateEventPayload";

interface NormalizeEventFormChatStateInput {
  data: FormChatState;
  terreiroId: string;
}

export const normalizeEventFormChatState = ({
  data,
  terreiroId,
}: NormalizeEventFormChatStateInput): FormChatState => {
  const isAllDay = data.isAllDay === true;
  const checkinStart = data.checkinStartWindowMinutes ?? 10;
  const checkinEnd = data.checkinEndWindowMinutes ?? 15;

  return {
    ...data,
    terreiroId,
    checkinStartWindowMinutes: checkinStart,
    checkinEndWindowMinutes: checkinEnd,
    startTime: isAllDay && !data.startTime ? "00:00:00" : data.startTime,
    endTime: isAllDay && !data.endTime ? "23:59:59" : data.endTime,
  };
};

interface BuildEventFormChatPayloadInput {
  data: FormChatState;
  terreiroId: string;
  memberFromActivityMap?: Record<string, boolean>;
  insumoFromActivityMap?: Record<string, boolean>;
}

export const buildEventFormChatPayload = ({
  data,
  terreiroId,
  memberFromActivityMap = {},
  insumoFromActivityMap = {},
}: BuildEventFormChatPayloadInput) => {
  if (!data.name || !data.type) return null;

  const location = data.location as BuildCreateEventPayloadInput["location"] | undefined;
  const resolvedLocation =
    location ??
    ({
      isInTerreiro: data.isInTerreiro ?? true,
      street: "",
      number: "",
      neighborhood: "",
      city: "",
      state: "",
    } as BuildCreateEventPayloadInput["location"]);

  const members = (data.memberIds ?? []).map((id) => ({
    id,
    fromActivity: memberFromActivityMap[id] === true,
  }));

  const insumos = (data.insumos ?? []).map((item) => ({
    id: item.id,
    quantity: item.quantity,
    fromActivity: insumoFromActivityMap[item.id] === true,
  }));

  const activities =
    (data.activities as Array<{
      id: string;
      startTime?: string;
      endTime?: string;
      checkinStartWindowMinutes?: number;
      checkinEndWindowMinutes?: number;
    }>) ?? [];

  return buildCreateEventPayload({
    name: data.name,
    type: String(data.type),
    description: data.description as string | undefined,
    terreiroId,
    date: data.date,
    isAllDay: data.isAllDay,
    startTime: data.startTime,
    endTime: data.endTime,
    isTemplate: data.isTemplate,
    qrCheckinEnabled: data.qrCheckinEnabled,
    checkinStartWindowMinutes: data.checkinStartWindowMinutes,
    checkinEndWindowMinutes: data.checkinEndWindowMinutes,
    location: resolvedLocation,
    members,
    insumos,
    activities,
    requiredActivityIds: data.requiredActivityIds as string[] | undefined,
    minConfirmedMembers: data.minConfirmedMembers,
  });
};
